import React from 'react';
import PropTypes from 'prop-types';
import { injectIntl } from 'react-intl';
import Helmet from 'react-helmet';
// import { graphql } from 'gatsby';
import {
  Label,
  Button
} from 'mw-style-react';
import DateUtils from '../../utils/dateUtils';
import Layout from '../../components/Layout/index';
import { Link, withIntl } from '../../i18n/index';
import './Post.scss';
import iconArrow from '../../images/arrow.svg';
import Utils from '../../utils/utils';
import PostItem from '../../components/PostItem/PostItem';
import Image from '../../components/Image/Image';

// Networks whose share dialog cannot be pre-filled from a URL: Facebook dropped
// the `quote` parameter and LinkedIn's share-offsite only accepts `url` (its
// feed card then shows just og:title + domain, no description). For these the
// post blurb is copied to the clipboard on click so the author can paste it
// into the composer. X/Twitter and email get the text from ShareThis' data-*.
const SHARE_COPY_NETWORKS = ['facebook', 'linkedin'];
const SHARE_HINT_TEXT = 'Blurb copied \u2014 paste it into your post';
const SHARE_HINT_MS = 6000;

// Cut `text` to at most `max` chars on a word boundary, adding an ellipsis.
function truncateWords(text, max) {
  if (!text || text.length <= max) return text || '';
  const cut = text.substring(0, max);
  const lastSpace = cut.lastIndexOf(' ');
  return `${(lastSpace > max / 2 ? cut.substring(0, lastSpace) : cut).replace(/[\s,.;:]+$/, '')}\u2026`;
}

class Post extends React.Component {
  componentWillMount() {
    if (typeof window !== 'undefined') {
      window.__sharethis__ = null;
      Utils.addTag('script', {innerText: `
        window.shar = (function(d, s, id) {
          var js, fjs = d.getElementsByTagName(s)[0],
            t = {};
          js = d.createElement(s);
          js.id = id;
          js.src = '//platform-api.sharethis.com/js/sharethis.js#property=5bb1e6f67c574000111ca540&product=inline-share-buttons';
          fjs.parentNode.insertBefore(js, fjs);
          t._e = [];
          t.ready = function(f) {
            console.log('ready', f)
          };
          return t;
        }(document, "script", "shar"));
      `});
    }
  }

  componentDidMount() {
    if (typeof document === 'undefined') return;

    // Capture phase so the blurb is on the clipboard before ShareThis opens
    // the network's share window (still inside the user gesture).
    this.handleShareClick = this.handleShareClick.bind(this);
    document.addEventListener('click', this.handleShareClick, true);

    const iconCopy = `<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><rect x="9" y="9" width="13" height="13" rx="2" ry="2"></rect><path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"></path></svg>`;
    const iconCheck = `<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#22c55e" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><polyline points="20 6 9 17 4 12"></polyline></svg>`;
    const iconCode = `<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><polyline points="16 18 22 12 16 6"></polyline><polyline points="8 6 2 12 8 18"></polyline></svg>`;

    document.querySelectorAll('.post pre').forEach(pre => {
      const wrapper = document.createElement('div');
      wrapper.className = 'code-block-wrapper';
      pre.parentNode.insertBefore(wrapper, pre);
      wrapper.appendChild(pre);

      const code = pre.querySelector('code');
      const langMatch = code && code.className.match(/language-(\w+)/);
      const lang = langMatch
        ? langMatch[1].charAt(0).toUpperCase() + langMatch[1].slice(1)
        : 'Code';

      const header = document.createElement('div');
      header.className = 'code-block-header';
      header.innerHTML = `
        <div class="code-block-lang">${iconCode}<span>${lang}</span></div>
        <button class="code-copy-btn" title="Copy">${iconCopy}</button>
      `;
      wrapper.insertBefore(header, pre);

      const btn = header.querySelector('.code-copy-btn');
      btn.addEventListener('click', () => {
        const text = (code || pre).innerText;
        const finish = () => {
          btn.innerHTML = iconCheck;
          setTimeout(() => { btn.innerHTML = iconCopy; }, 2000);
        };
        if (navigator.clipboard) {
          navigator.clipboard.writeText(text).then(finish);
        } else {
          const ta = document.createElement('textarea');
          ta.value = text;
          document.body.appendChild(ta);
          ta.select();
          document.execCommand('copy');
          document.body.removeChild(ta);
          finish();
        }
      });
    });
  }

  componentWillUnmount() {
    if (typeof document === 'undefined') return;
    document.removeEventListener('click', this.handleShareClick, true);
    clearTimeout(this.shareHintTimer);
  }

  // Text put on the clipboard for Facebook / LinkedIn shares: the post blurb
  // (same text as og:description) followed by the canonical post URL.
  getShareBlurb() {
    const { fields, frontmatter } = this.props.pageContext.post.node;
    const summary = frontmatter.metaDescription || frontmatter.description || '';
    return `${summary}\n\nhttps://corezoid.com${fields.slug}`;
  }

  handleShareClick(e) {
    const target = e.target;
    if (!target || typeof target.closest !== 'function') return;
    const btn = target.closest('.sharethis-inline-share-buttons .st-btn');
    if (!btn) return;
    const network = btn.getAttribute('data-network');
    if (SHARE_COPY_NETWORKS.indexOf(network) === -1) return;

    const text = this.getShareBlurb();
    const wrapper = btn.closest('.post__share-box');
    const finish = () => this.showShareHint(wrapper);
    const fallbackCopy = () => {
      const ta = document.createElement('textarea');
      ta.value = text;
      ta.setAttribute('readonly', '');
      ta.style.position = 'fixed';
      ta.style.opacity = '0';
      document.body.appendChild(ta);
      ta.select();
      try { document.execCommand('copy'); } catch (err) { /* clipboard unavailable */ }
      document.body.removeChild(ta);
      finish();
    };
    if (navigator.clipboard && navigator.clipboard.writeText) {
      navigator.clipboard.writeText(text).then(finish, fallbackCopy);
    } else {
      fallbackCopy();
    }
  }

  showShareHint(wrapper) {
    if (!wrapper) return;
    let hint = wrapper.querySelector('.post__share-hint');
    if (!hint) {
      hint = document.createElement('span');
      hint.className = 'post__share-hint';
      hint.setAttribute('role', 'status');
      hint.textContent = SHARE_HINT_TEXT;
      wrapper.appendChild(hint);
    }
    hint.classList.add('post__share-hint--visible');
    clearTimeout(this.shareHintTimer);
    this.shareHintTimer = setTimeout(() => {
      hint.classList.remove('post__share-hint--visible');
    }, SHARE_HINT_MS);
  }

  renderPosts(posts, type, activePostDate, intl) {
    const copyPosts = posts.slice();
    // удаление из списка постов котороая отображается
    copyPosts.splice(copyPosts.findIndex(post => post.node.frontmatter.date === activePostDate), 1);
    // количество отображаемых статей
    const maxPostsList = 9;
    // отображаем только поервые n-постов
    const renderPosts = copyPosts.splice(0, maxPostsList);

    posts = renderPosts.map((p, index) => {
      const { date, title, url} = p.node.frontmatter;

      return (
        <div key={index}>
          <Link
            to={`/${type}/${url}`}
            className="post__item"
          >
            <Label
              value={title}
              className="post__item__title"
            />
            <Label
              value={DateUtils.toDate((new Date(date) / 1000), 'DD M YYYY', intl.locale)}
              className="post__item__date"
            />
          </Link>
        </div>
      );
    });
    return posts;
  }

  render() {
    const { intl, pageContext } = this.props;
    const { posts, post, type } = pageContext;
    const { html, fields, frontmatter } = post.node;
    const { date, title, image, description, tags } = frontmatter;
    const { metaTitle, metaDescription, author, citation } = frontmatter;

    const renderPost = posts.filter(post => post.node.frontmatter.lang === intl.locale);
    const favoritePost =
      renderPost.filter(post => post.node.frontmatter.url === this.props.pageContext.next)[0];

    // Fallback for posts without metaDescription: the blurb cut on a word
    // boundary (~160 chars fits the og/description limits of every network).
    const descr = truncateWords(description, 160);
    // SEO overrides: posts may ship their own meta title/description in frontmatter,
    // otherwise we fall back to the post title and the shortened description.
    const seoTitle = metaTitle || title;
    const seoDescr = metaDescription || descr;
    const postUrl = `https://corezoid.com${fields.slug}`;
    const imageUrl = `https://corezoid.com${image}`;
    const structuredData = {
      '@context': 'https://schema.org',
      '@type': 'NewsArticle',
      headline: title,
      description: seoDescr,
      image: [imageUrl],
      datePublished: date,
      mainEntityOfPage: {
        '@type': 'WebPage',
        '@id': postUrl
      },
      author: {
        '@type': author ? 'Person' : 'Organization',
        name: author || 'Corezoid Inc.'
      },
      publisher: {
        '@type': 'Organization',
        name: 'Corezoid Inc.',
        url: 'https://corezoid.com'
      }
    };
    if (citation) {
      structuredData.citation = citation;
    }
    const isABC = fields.slug.indexOf('middleware-offers-apple-business-chat') !== -1;
    const isFranceLocale = fields.slug.indexOf('operation-defragmentation-fr') !== -1;
    const activeLocale = isFranceLocale ? 'fr' : intl.locale;

    return (
      <Layout page={type}>
        <Helmet>
          <meta property="og:type" content="article" />
          <meta property="og:url" content={postUrl} />
          <meta property="og:title" content={seoTitle} />
          <meta property="og:description" content={seoDescr} />
          <meta property="og:image" content={imageUrl} />
          <meta property="article:published_time" content={date} />
          <meta name="twitter:card" content="summary_large_image" />
          <meta name="twitter:title" content={seoTitle} />
          <meta name="twitter:description" content={seoDescr} />
          <meta name="twitter:image" content={imageUrl} />
          <title>
            {seoTitle}
          </title>
          <meta
            name="description"
            content={seoDescr}
          />
          <script type="application/ld+json">
            {JSON.stringify(structuredData)}
          </script>
        </Helmet>
        <div className="post__header blog__header__top"/>
        <div className="grid-wrapper post">
          <div className="col-9">
            <div
              className="post__title"
            >
              <h1>{title}</h1>
            </div>
            <div className="post__info">
              <Label
                value={DateUtils.toDate((new Date(date) / 1000), 'DD M YYYY', activeLocale)}
                className="post__info__date"
              />
              <div className="post__share-box">
                <div
                  className="sharethis-inline-share-buttons"
                  data-url={postUrl}
                  data-title={seoTitle}
                  data-description={seoDescr}
                  data-image={imageUrl}
                />
              </div>
            </div>
            <div dangerouslySetInnerHTML={{ __html: html }} />
            <div className="post__footer">
              <div className="post__footer__tags" onClick={(e) => e.stopPropagation()}>
                {tags.map(tag => tag && (
                  <Link className="article__tags__item" to={`/blog/${tag}`}>{`#${tag} `}</Link>
                ))}
              </div>
              <div className="post__footer__share post__share-box">
                <div
                  className="sharethis-inline-share-buttons"
                  data-url={postUrl}
                  data-title={seoTitle}
                  data-description={seoDescr}
                  data-image={imageUrl}
                />
              </div>
            </div>
            {isABC ?
              <Link className="post__recommended" to={'/bot-platform/apple-messages-for-business'}>
                <div className="post__recommended__abc">
                  <div className="post__recommended__abc__icon">
                    <Image file={'external-link.svg'}/>
                  </div>
                  <div className="post__recommended__abc__image">
                    <Image file={'abc.svg'}/>
                  </div>
                  <div className="post__recommended__abc__text">
                    <span className="post__recommended__abc__title">{intl.formatMessage({id: 'abcSubTitle'})}</span>
                    <span>{intl.formatMessage({id: 'abcText'})}</span>
                  </div>
                </div>
              </Link> :
              <div>
                <Label
                  value={intl.formatMessage({id: 'blogRecommendedArticle'})}
                  className="post__recommended"
                />
                {favoritePost &&
                  <PostItem
                    data={favoritePost.node}
                    type="post"
                  />
                }
              </div>
            }
          </div>
          <div className="col-3 post__rightList">
            {this.renderPosts(renderPost, type, date, intl)}
            <Link
              to={`/${type}`}
              className="post__all"
            >
              <Label
                value={intl.formatMessage({id: 'blogViewAll'})}
                className="post__all__text"
              />
              <img src={iconArrow} alt="More"/>
            </Link>
          </div>
        </div>
        <div className="post__demo">
          <div className="post__demo__wrapper">
            <div className="post__demo__title">
              {intl.formatMessage({id: 'postDemoTitle'})}
            </div>
            <div className="post__demo__buttons">
              <Link to={intl.formatMessage({id: 'postDemoBtnCreateLink'})}>
                <Button
                  className="button__default post__demo__btn"
                  label={intl.formatMessage({id: 'postDemoBtnCreate'})}
                />
              </Link>
              <Link to={'/form/live-demo'}>
                <Button
                  className="button__secondary post__demo__btn"
                  label={intl.formatMessage({id: 'postDemoBtnDemo'})}
                />
              </Link>
            </div>
          </div>
        </div>
      </Layout>
    );
  }
}

Post.propTypes = {
  pageContext: PropTypes.object,
  intl: PropTypes.object
};

export default withIntl(injectIntl(Post));
